/**
 * EventViewer (PULSE-268) — просмотр событий скринера с графиком.
 *
 * Список событий (бектест или живые сигналы) + MarketChart вокруг
 * выбранного события (маркер «сигнал», 1/3 контекста до / 2/3 после),
 * переключение «новее/старее» кнопками и стрелками ←/→, свитчеры
 * таймфрейма свечей и окна. Используется страницей /app/screeners/:id
 * и модалкой в ленте сигналов.
 */
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'

import { MarketChart } from '@/components/market/MarketChart'
import type { MarketSeries } from '@/hooks/useMarketApi'
import { type BacktestEvent, useEventChart } from '@/hooks/useScreenersApi'
import { cn } from '@/lib/cn'

const WINDOWS = [
  { key: '1h', label: '1ч' },
  { key: '4h', label: '4ч' },
  { key: '24h', label: '24ч' },
]

const TFS = [
  { key: 'auto', label: 'авто' },
  { key: '1m', label: '1м' },
  { key: '5m', label: '5м' },
  { key: '15m', label: '15м' },
  { key: '30m', label: '30м' },
]

export function fmtPct(v: number | string | null | undefined): string {
  if (v == null || typeof v === 'string') return String(v ?? '—')
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`
}

export function eventSummary(e: BacktestEvent): string {
  const m = e.metrics
  const parts: string[] = []
  if (m.price_pct != null) parts.push(`P ${fmtPct(m.price_pct as number)}/${m.price_window_min}м`)
  if (m.oi_pct != null) parts.push(`OI ${fmtPct(m.oi_pct as number)}/${m.oi_window_min}м`)
  if (m.nd_pct != null) parts.push(`Δ ${fmtPct(m.nd_pct as number)}/${m.nd_window_min}м`)
  if (m.gap_pct != null) parts.push(`Гап ${fmtPct(m.gap_pct as number)}/${m.gap_window_min}м`)
  if (m.vol_x != null) parts.push(`Объём ×${m.vol_x}/${m.vol_window_min}м`)
  if (m.liq_usd != null) parts.push(`Ликв. $${Math.round(Number(m.liq_usd) / 1000)}k/${m.liq_window_min}м`)
  if (m.fund_pct != null) parts.push(`Фандинг ${m.fund_pct}%`)
  if (!parts.length) {
    if (m.direction) parts.push(String(m.direction))
    if (m.dp_pct != null) parts.push(`dp ${fmtPct(m.dp_pct as number)}`)
    if (m.oi_change_pct != null) parts.push(`oi ${fmtPct(m.oi_change_pct as number)}`)
  }
  return parts.join(' · ') || '—'
}

export function EventViewer({ events, index, setIndex }: {
  events: BacktestEvent[]
  index: number
  setIndex: (i: number) => void
}) {
  const [win, setWin] = useState('4h')
  const [tf, setTf] = useState('auto')
  const ev = events[index] ?? null
  const { data, isLoading, isError } = useEventChart(
    ev?.symbol ?? null, ev?.ts ?? null, win, tf)

  // стрелки клавиатуры: ← старее не значит… ← = предыдущее (свежее в списке выше)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1)
      if (e.key === 'ArrowRight' && index < events.length - 1) setIndex(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, events.length, setIndex])

  if (!ev) return null
  return (
    <div className="bg-ink border border-line">
      <div className="flex items-center gap-3 px-3 py-2 border-b border-line flex-wrap">
        <button
          onClick={() => setIndex(index - 1)}
          disabled={index <= 0}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-textDim border border-line hover:bg-line/20 disabled:opacity-40"
        >
          <ChevronLeft size={14} /> новее
        </button>
        <span className="text-xs text-textFaint tabular-nums">
          {index + 1} / {events.length}
        </span>
        <button
          onClick={() => setIndex(index + 1)}
          disabled={index >= events.length - 1}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-textDim border border-line hover:bg-line/20 disabled:opacity-40"
        >
          старее <ChevronRight size={14} />
        </button>

        <span className="font-semibold ml-2">{ev.symbol}</span>
        <span className="text-xs text-textDim">
          {new Date(ev.ts).toLocaleString('ru-RU', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
          })}
        </span>
        <span className="text-xs text-textDim">{eventSummary(ev)}</span>

        <div className="flex items-center gap-2 ml-auto">
          <div className="flex border border-line" title="Таймфрейм свечей">
            {TFS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTf(t.key)}
                className={cn('px-2 py-1 text-xs',
                  tf === t.key ? 'bg-line/40 text-text' : 'text-textDim hover:text-text')}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex border border-line" title="Окно вокруг сигнала">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWin(w.key)}
                className={cn('px-2 py-1 text-xs',
                  win === w.key ? 'bg-line/40 text-text' : 'text-textDim hover:text-text')}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-[80vh] min-h-[760px]">
        {isLoading && (
          <div className="h-full flex items-center justify-center text-textFaint text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> график…
          </div>
        )}
        {isError && (
          <div className="h-full flex items-center justify-center text-rose text-sm">
            Не удалось загрузить график.
          </div>
        )}
        {data && !isLoading && (
          <MarketChart
            data={data.series as MarketSeries}
            markers={data.signal_marker ? { direction: '', signal: data.signal_marker } : undefined}
          />
        )}
      </div>
    </div>
  )
}

